import React from "react";
import { Formik, Form } from "formik";
import { TextField, Button, Box, Grid, FormControl, Autocomplete, Typography } from "@mui/material";
import axios from "../../api";
import SoftTypography from "components/SoftTypography";
import * as Yup from "yup";
import Locations from "examples/ReuseFunctions/Locations";
import { AxiosError } from "axios";

const customScrollbarStyle = {
  "&::-webkit-scrollbar": { width: "8px" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#44444461",
    borderRadius: "8px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#666",
  },
};

// Formik validation
const validationSchema = Yup.object({
  state_id: Yup.string().required("State is required"),
  city_id: Yup.string().required("City is required"),
  region_id: Yup.string().required("Region is required"),
  address_type: Yup.string().required("Address type is required"),
  address: Yup.string().required("Address is required"),
  division_id: Yup.string().when("customerType", ([customerType], schema) => {
    return customerType === "company"
      ? schema.required("Division is required")
      : schema.notRequired().nullable();
  }),
});

// Types
interface State {
  state_id: string;
  state_name: string;
}

interface City {
  city_id: string;
  city_name: string;
}

interface Region {
  region_id: string;
  region_name: string;
}

interface AddAddressProps {
  fetchSuccess: (msg: string) => void;
  fetchError: (msg: string) => void;
  onClose: () => void;
  customerId: string;
  customerType: string;
  fetchData: () => Promise<void>;
}

interface FormValues {
  state_id: string;
  city_id: string;
  region_id: string;
  address_type: string;
  address: string;
  division_id: string | null;
}
interface Division {
  division_id: number;
  division_name: string;
}

const AddAddress: React.FC<AddAddressProps> = ({
  fetchSuccess,
  fetchError,
  onClose,
  customerId,
  customerType,
  fetchData,
}) => {
  const { states, cities, areas, setSelectedState, setSelectedCity } =
    Locations();
  const [divisions, setDivisions] = React.useState<Division[]>([]);
  React.useEffect(() => {
    if (customerId) {
      axios
        .get(`/divisions/customer/${customerId}`)
        .then((res) => {
          setDivisions(res.data.divisions || []);
        })
        .catch(() => setDivisions([]));
    }
  }, [customerId]);

  return (
    <Box
      sx={{
        minWidth: 500,
        maxWidth: "100%",
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        component="p"
        sx={{ display: "block", width: "100%", mb: 2 }}
      >
        Add a new address for this customer.
      </Typography>
      <Formik<FormValues>
          initialValues={{
            state_id: "",
            city_id: "",
            region_id: "",
            address_type: "",
            address: "",
            division_id: customerType === "company" ? "" : null,
          }}
          validationSchema={validationSchema}
          onSubmit={async (values) => {
            try {
              const payload = {
                ...values,
                customer_id: customerId,
                division_id:
                  customerType === "company"
                    ? values.division_id || null
                    : null,
              };

              const response = await axios.post("/address", payload);
              fetchSuccess(response.data.message);
              fetchData();
              onClose();
            } catch (error) {
              if (error instanceof AxiosError && error.response) {
                fetchError(error.response.data.error);
              } else {
                fetchError("Something went wrong");
              }
            }
          }}
        >
        {({ values, handleChange, setFieldValue, errors, touched }) => (
          <Form>
            <Grid container spacing={2}>
                {customerType === "company" && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Division
                      </SoftTypography>
                      <Autocomplete
                        options={divisions || []}
                        getOptionLabel={(option) => option.division_name || ""}
                        value={
                          divisions.find(
                            (d) => d.division_id === Number(values.division_id)
                          ) || null
                        }
                        onChange={(_, newValue) =>
                          setFieldValue(
                            "division_id",
                            newValue?.division_id || ""
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name="division_id"
                            variant="outlined"
                            error={!!errors.division_id && touched.division_id}
                            helperText={
                              touched.division_id && errors.division_id
                            }
                            fullWidth
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                )}

                {/* State */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      State
                    </SoftTypography>
                    <Autocomplete
                      options={states ?? []}
                      getOptionLabel={(option: State) =>
                        option.state_name || ""
                      }
                      value={
                        (states as unknown as any[])?.find(
                          (state: { state_id: string }) =>
                            state.state_id === values.state_id
                        ) || null
                      }
                      onChange={(_, newValue) => {
                        setFieldValue("city_id", "");
                        setFieldValue("region_id", "");
                        setFieldValue("state_id", newValue?.state_id || "");
                        setSelectedState(
                          newValue ? Number(newValue.state_id) : null
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="state_id"
                          variant="outlined"
                          error={!!errors.state_id && touched.state_id}
                          helperText={touched.state_id && errors.state_id}
                          fullWidth
                        />
                      )}
                    />
                  </FormControl>
                </Grid>

                {/* City */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      City
                    </SoftTypography>
                    <Autocomplete
                      options={cities ?? []}
                      getOptionLabel={(option: City) => option.city_name || ""}
                      value={
                        (cities as unknown as any[])?.find(
                          (city) => city.city_id === values.city_id
                        ) || null
                      }
                      onChange={(_, newValue) => {
                        setFieldValue("region_id", "");
                        setFieldValue("city_id", newValue?.city_id || "");
                        setSelectedCity(
                          newValue ? Number(newValue.city_id) : null
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="city_id"
                          variant="outlined"
                          error={!!errors.city_id && touched.city_id}
                          helperText={touched.city_id && errors.city_id}
                          fullWidth
                        />
                      )}
                    />
                  </FormControl>
                </Grid>

                {/* Region */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Area
                    </SoftTypography>
                    <Autocomplete
                      options={areas ?? []}
                      getOptionLabel={(option: Region) =>
                        option.region_name || ""
                      }
                      value={
                        (areas as unknown as any[])?.find(
                          (region) => region.region_id === values.region_id
                        ) || null
                      }
                      onChange={(_, newValue) =>
                        setFieldValue("region_id", newValue?.region_id || "")
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="region_id"
                          variant="outlined"
                          error={!!errors.region_id && touched.region_id}
                          helperText={touched.region_id && errors.region_id}
                          fullWidth
                        />
                      )}
                    />
                  </FormControl>
                </Grid>

                {/* Address Type - label above, input below */}
                <Grid item xs={12}>
                  <Box sx={{ display: "block", width: "100%" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Address Type
                    </SoftTypography>
                    <TextField
                      fullWidth
                      name="address_type"
                      value={values.address_type}
                      onChange={handleChange}
                      error={!!errors.address_type && touched.address_type}
                      helperText={touched.address_type && errors.address_type}
                      variant="outlined"
                    />
                  </Box>
                </Grid>

                {/* Address - label above, input below */}
                <Grid item xs={12}>
                  <Box sx={{ display: "block", width: "100%" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Address
                    </SoftTypography>
                    <TextField
                      fullWidth
                      name="address"
                      value={values.address}
                      onChange={handleChange}
                      error={!!errors.address && touched.address}
                      helperText={touched.address && errors.address}
                      variant="outlined"
                    />
                  </Box>
                </Grid>

                {/* Buttons */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Box display="flex" gap={2}>
                    <Button
                      className="success-btn"
                      sx={{
                        position: "relative",
                        minWidth: 100,
                      }}
                      variant="contained"
                      type="submit"
                      color="success"
                    >
                      Save
                    </Button>
                    <Button
                      className="cancel-btn"
                      sx={{
                        position: "relative",
                        minWidth: 100,
                        ml: 2.5,
                      }}
                      variant="contained"
                      color="warning"
                      onClick={onClose}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Box>
  );
};

export default AddAddress;
